import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ContactModal from '../components/ContactModal';
import ServiceCard from '../components/home/ServiceCard';
import StatCard from '../components/home/StatCard';
import ProtocolStep from '../components/home/ProtocolStep';

const { 
  FiTruck, FiUsers, FiDollarSign, FiMapPin, FiBook, 
  FiMonitor, FiSettings, FiShield, FiWrench, 
  FiArrowRight, FiActivity, FiZap, FiCheck 
} = FiIcons;

const Home = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const services = [
    { icon: FiDollarSign, title: "Accounting", description: "Tax strategy and cash flow optimization designed specifically for carrier margins." },
    { icon: FiUsers, title: "Recruiting", description: "Hiring at scale. We source, vet, and qualify elite drivers with proven safety records." },
    { icon: FiTruck, title: "Rentals", description: "Access to high-spec power units and trailers without the predatory lease terms." },
    { icon: FiMapPin, title: "Parking", description: "Exclusive access to our secure, 24/7 monitored parking network across major hubs." },
    { icon: FiBook, title: "Logbooks", description: "Rigorous compliance management to keep your DOT scores in the green zone." },
    { icon: FiMonitor, title: "ELD Solutions", description: "Next-gen telematics that do more than track—they predict maintenance needs." },
    { icon: FiSettings, title: "TMS", description: "Enterprise-grade operational software to automate dispatch and invoicing." },
    { icon: FiShield, title: "Safety", description: "Comprehensive risk mitigation and Mock DOT audits by former inspectors." },
    { icon: FiWrench, title: "Maintenance", description: "Priority shop access and 24/7 roadside assistance that actually shows up." }
  ];

  const stats = [
    { value: "$42M+", label: "Fuel Savings Found", icon: FiZap },
    { value: "1,200+", label: "Drivers Recruited", icon: FiUsers },
    { value: "300k+", label: "Safe Miles Monitored", icon: FiShield },
    { value: "98%", label: "Client Retention", icon: FiActivity }
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-white selection:bg-yellow-400 selection:text-black">
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

      {/* Navigation */}
      <nav className="fixed w-full top-0 z-50 bg-black/95 border-b border-zinc-800 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <div className="bg-yellow-400 p-2 rounded-lg mr-3">
                <SafeIcon icon={FiTruck} className="h-6 w-6 text-black" />
              </div>
              <span className="text-3xl font-black tracking-tighter uppercase italic">Trukr</span>
            </div>

            <div className="hidden md:flex space-x-10 text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500">
              <button onClick={() => scrollToSection('services')} className="hover:text-yellow-400 transition-colors py-2 outline-none">Services</button>
              <button onClick={() => scrollToSection('protocol')} className="hover:text-yellow-400 transition-colors py-2 outline-none">Protocol</button>
              <button onClick={() => scrollToSection('about')} className="hover:text-yellow-400 transition-colors py-2 outline-none">History</button>
            </div>

            <button onClick={() => setIsModalOpen(true)} className="bg-yellow-400 text-black px-6 py-2.5 rounded-lg font-black uppercase text-xs tracking-widest hover:bg-white transition-all shadow-lg active:scale-95">
              Intro Call
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent z-10" />
          <img src="https://images.unsplash.com/photo-1519003722824-194d4455a60c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" className="w-full h-full object-cover grayscale opacity-40" alt="Truck" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl">
            <div className="inline-flex items-center space-x-3 mb-6 bg-red-600/10 border border-red-600/20 px-4 py-2 rounded-full">
              <span className="h-2 w-2 bg-red-600 rounded-full animate-pulse" />
              <span className="text-red-500 font-black uppercase tracking-[0.2em] text-[10px]">Operational Excellence Since 1996</span>
            </div>
            <h1 className="text-6xl md:text-9xl font-black uppercase tracking-tighter leading-[0.85] mb-8">
              The <span className="text-yellow-400 italic">Unfair</span> Advantage <br /> For Your Fleet.
            </h1>
            <p className="text-xl md:text-2xl text-zinc-400 mb-10 leading-relaxed font-medium max-w-2xl">
              Built by <span className="text-white font-bold">industry veterans</span> who have lived and breathed trucking since the analog days. We eliminate the noise and focus on one metric: <span className="text-white">Your EBITDA.</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-5">
              <button onClick={() => setIsModalOpen(true)} className="group bg-red-600 text-white px-10 py-5 rounded-xl text-xl font-black uppercase tracking-tighter hover:bg-red-700 transition-all flex items-center justify-center shadow-2xl">
                Start Optimization <SafeIcon icon={FiArrowRight} className="ml-3 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-32 bg-zinc-900 border-y border-zinc-800 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-24">
            {stats.map((stat, i) => <StatCard key={i} {...stat} />)}
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <h2 className="text-5xl font-black uppercase tracking-tighter mb-4 italic">The Arsenal</h2>
              <p className="text-zinc-500 text-lg max-w-xl font-medium">Modular high-performance services that scale with your fleet size.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((s, i) => <ServiceCard key={i} {...s} />)}
          </div>
        </div>
      </section>

      {/* Protocol Section */}
      <section id="protocol" className="py-32 bg-black scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-black uppercase tracking-tighter mb-4">The Trukr Protocol</h2>
            <p className="text-zinc-500 max-w-2xl mx-auto font-medium">How we transition your fleet from surviving to dominant market leaders.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <ProtocolStep number="01" title="Deep Audit" desc="We analyze your last 24 months of data to find hidden leaks in fuel, insurance, and maintenance." />
            <ProtocolStep number="02" title="Infrastructure" desc="We deploy our proprietary TMS and ELD stacks to give you real-time visibility on every cent." />
            <ProtocolStep number="03" title="Scale Engine" desc="We turn on the recruiting and rental pipelines to grow your fleet without the typical growing pains." />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-zinc-950 relative overflow-hidden scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 lg:grid lg:grid-cols-2 gap-20 items-center">
          <div>
            <h2 className="text-5xl font-black uppercase tracking-tighter mb-8 leading-none">
              30 Years of <br /> <span className="text-red-600">Road-Proven</span> Mastery
            </h2>
            <p className="text-xl text-zinc-400 mb-8 leading-relaxed font-medium">
              We're not just consultants. We're <span className="text-white">industry veterans</span> who have lived and breathed trucking since the analog days. Our team brings three decades of road-proven expertise to your fleet operations.
            </p>
            <div className="space-y-4 mb-10">
              {['Former DOT Inspectors on Staff', 'Proprietary Fuel Rebate Network', '24/7/365 US-Based Support Center'].map((item, i) => (
                <div key={i} className="flex items-center space-x-3 text-zinc-300 font-bold uppercase text-xs tracking-widest">
                  <div className="bg-yellow-400 p-1 rounded"><SafeIcon icon={FiCheck} className="text-black w-3 h-3" /></div>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src="https://images.unsplash.com/photo-1586333242963-2ca5044f675b?auto=format&fit=crop&w=400&q=80" className="rounded-2xl grayscale" alt="Expertise" />
            <img src="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=400&q=80" className="rounded-2xl pt-12" alt="Fleet" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-zinc-900 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-10 mb-12">
            <div className="flex items-center cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <SafeIcon icon={FiTruck} className="h-8 w-8 text-yellow-400 mr-3" />
              <span className="text-4xl font-black uppercase italic">Trukr</span>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="text-center text-zinc-600 text-[10px] font-black uppercase tracking-[0.3em] mb-4">
                © 2026 Trukr Industry Solutions. All rights reserved. <br />
                <span className="text-zinc-800">Built by operators, for operators.</span>
              </div>
              {/* LEGAL LINKS */}
              <div className="flex space-x-8">
                <Link to="/terms" className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500 hover:text-yellow-400 transition-colors">Terms & Conditions</Link>
                <Link to="/privacy" className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500 hover:text-yellow-400 transition-colors">Privacy Policy</Link>
              </div>
            </div>

            <button onClick={() => setIsModalOpen(true)} className="px-8 py-3 border-2 border-zinc-800 rounded-full hover:border-yellow-400 transition-colors font-black uppercase text-xs tracking-widest active:scale-95">
              Contact us
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;